import { useEffect, useRef, useState } from 'react'



import * as Sentry from '@sentry/react'
import _ from 'lodash'
import { useSession as useNextAuthSession } from '@/shared/services/session/compat'


import { AuthModes, type AuthMode } from '@/features/auth/policy'
import { QRCodesWatcher } from '@/features/check-in/QRWatcher'
import { BonusSheetWatcher } from '@/features/bonus'
import { CheckInWatcher } from '@/features/check-in/Watcher'
import { CsatWatcher } from '@/features/csat'
import { AddInfoSurvey } from '@/features/user/additional-information/Survey'
import { HeaderProfileArea } from '@/features/user/layout/HeaderProfileArea'
import { BottomSheetProvider } from '@/shared/contexts/BottomSheetContext'
import { DigitalProductProvider } from '@/shared/contexts/DigitalProductContext'
import { LayoutChromeProvider } from '@/shared/contexts/LayoutChromeContext'
import { useRenderContext } from '@/shared/contexts/RenderContext'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useDigitalProduct } from '@/shared/hooks/useDigitalProduct'
import { useDigitalProductBackground } from '@/shared/hooks/useDigitalProductBackground'
import { useAuthActions } from '@/shared/lib/auth/actions'
import { AnalyticsInitializer } from '@/shared/lib/integrations/analytics/AnalyticsInitializer'
import { SiteMeta } from '@/shared/lib/meta/page-meta'
import { authPaths, toRelativeCallbackPath } from '@/shared/lib/routing/auth'
import { AvatarProvider } from '@/shared/ui/Avatar'
import { BootFailureNotice } from '@/shared/ui/BootFailureNotice'
import { BootSkeleton } from '@/shared/ui/BootSkeleton'
import { GamificationDataInitializer } from '@/shared/ui/GamificationDataInitializer'
import { AppMenuProvider, Layout } from '@/shared/ui/Layout'
import { TopProgressBar } from '@/shared/ui/TopProgressBar'

export const Guard = ({ authMode = AuthModes.REQUIRED as AuthMode, requiresDigitalProductId = false, children }) => {
  const router = useAppRouter()
  const hasDigitalProductId =
    typeof router.query?.digitalProductId === 'string' &&
    router.query.digitalProductId.trim().length > 0
  const digitalProductId = hasDigitalProductId
    ? router.query.digitalProductId
    : undefined
  const authCallbackUrl = toRelativeCallbackPath(router.asPath)
  const isBootstrap = authMode === AuthModes.BOOTSTRAP
  const isPublicRoute = authMode === AuthModes.PUBLIC
  const requiresAuth = authMode === AuthModes.REQUIRED

  const { inNativeAppWebView } = useRenderContext()
  const authActions = useAuthActions()
  const nativeAuthRequestedRef = useRef(false)
  const browserRedirectRequestedRef = useRef(false)

  const { data: session, status: sessionStatus } = useNextAuthSession()
  const [sessionTimedOut, setSessionTimedOut] = useState(false)

  useEffect(() => {
    if (sessionStatus !== 'loading') {
      setSessionTimedOut(false)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      console.error(
        '[gamification-auth] session check timed out — BFF /api/auth/session may be unavailable'
      )
      setSessionTimedOut(true)
    }, 12_000)

    return () => window.clearTimeout(timeoutId)
  }, [sessionStatus])

  const sessionIsLoading = sessionStatus === 'loading' && !sessionTimedOut
  // Exigia `accessToken` e ausência de `error` para não renderizar a home por
  // um frame e quicar de volta ao login: o Auth.js podia reportar
  // `authenticated` antes de o token aterrissar, ou com um token que o
  // `RefreshAccessTokenError` já marcara como inutilizável.
  //
  // O `adopt()` do `@evnttz/session` grava o token **antes** de mudar o status,
  // então `authenticated` já implica token vivo; e uma renovação recusada limpa
  // o estado inteiro em vez de deixar a sessão de pé. A garantia é a mesma, sem
  // depender de um campo que a UI não recebe mais.
  const isAuthenticated =
    sessionStatus === 'authenticated' && !!session && !!session?.user

  try {
    Sentry.setUser(_.get(session, 'user'))
  } catch (error) {}

  const shouldFetchDigitalProduct =
    !sessionIsLoading && (!requiresDigitalProductId || hasDigitalProductId)

  const digitalProductAttrs = useDigitalProduct({
    shouldFetch: shouldFetchDigitalProduct
  })
  const {
    digitalProduct,
    isSuccess,
    errorStatusCode,
    failureCount,
    refetch
  } = digitalProductAttrs

  useDigitalProductBackground(digitalProduct, { withBackgroundImage: false })

  useEffect(() => {
    if (sessionIsLoading || !requiresAuth || isAuthenticated) {
      nativeAuthRequestedRef.current = false
      return
    }
    if (!inNativeAppWebView) return
    if (nativeAuthRequestedRef.current) return

    nativeAuthRequestedRef.current = true
    authActions.requireAuth(authCallbackUrl)
  }, [sessionIsLoading, requiresAuth, isAuthenticated, inNativeAppWebView, authCallbackUrl])

  useEffect(() => {
    if (sessionIsLoading || !requiresAuth || isAuthenticated) {
      browserRedirectRequestedRef.current = false
      return
    }
    if (inNativeAppWebView) return
    if (authCallbackUrl.startsWith('/auth/')) return
    if (browserRedirectRequestedRef.current) return

    browserRedirectRequestedRef.current = true
    router.replace(authPaths.signIn({ callbackUrl: authCallbackUrl }))
  }, [
    authCallbackUrl,
    inNativeAppWebView,
    isAuthenticated,
    requiresAuth,
    router,
    sessionIsLoading
  ])

  // Uma árvore só para carregando e carregado.
  //
  // O que muda entre os dois estados é o CONTEÚDO, não a estrutura: mesmos
  // providers, mesmo `Layout`, mesma posição. É isso que faz o shell não
  // remontar quando o evento chega — se o carregando fosse outra árvore, a
  // topbar desmontaria e a tela saltaria no exato momento em que o usuário
  // começa a ler.
  //
  // Não existe mais espera artificial. `MIN_GLOBAL_LOADER_MS` segurava o
  // loader por 900ms para esconder o flash de um frame da animação; sem a
  // animação no caminho crítico, o problema que ela mascarava não existe.
  //
  // A condição exige `isAuthenticated`, e NÃO basta a sessão estar
  // carregando. O chrome dispara requisição autenticada só de montar — a
  // wallet busca progresso, a bottomnav busca as páginas do produtor. Montado
  // antes de haver sessão, o 401 dessas chamadas aciona o interceptor de
  // recuperação, que faz sign-out, que navega para `/`, que monta o chrome de
  // novo: laço infinito para qualquer visitante deslogado. Enquanto a sessão
  // resolve, o que aparece é só a barra — sem shell, sem query.
  if (requiresAuth && isAuthenticated) {
    // O evento pronto é a única condição para renderizar feature: o hook
    // estrito lança sem ele, de propósito, e uma exceção sem boundary é tela
    // branca. Resposta com status de erro não é "ainda carregando" — é o
    // estado terminal, e é assim que aparece.
    const eventoPronto = Boolean(digitalProduct)
    const falhaTerminal = Boolean(errorStatusCode) && !eventoPronto
    const carregandoEvento = !eventoPronto

    return (
      <>
        {digitalProduct?.name && (
          <SiteMeta
            lang="pt_BR"
            defaultTitle={digitalProduct.name}
            titleTemplate={`%s | ${digitalProduct.name}`}
            faviconHref={_.get(
              digitalProduct,
              'favicon.file.url',
              '/placeholders/icon.png'
            )}
            description={digitalProduct?.description}
            metaTags={[
              { name: 'viewport', content: 'width=device-width' }
            ]}
          />
        )}
        <DigitalProductProvider {...digitalProductAttrs}>
          {carregandoEvento && !falhaTerminal && (
            <TopProgressBar attention={failureCount > 0} />
          )}
          <BootFailureNotice
            failureCount={failureCount}
            terminal={falhaTerminal}
            onRetry={refetch}
          />
          <AppMenuProvider>
            <LayoutChromeProvider
              value={{ HeaderProfileArea: HeaderProfileArea }}
            >
              <BottomSheetProvider>
                <AvatarProvider>
                  <Layout>
                    {carregandoEvento ? <BootSkeleton /> : children}
                  </Layout>
                </AvatarProvider>
              </BottomSheetProvider>
            </LayoutChromeProvider>
          </AppMenuProvider>
          {/* Watchers e inicializadores dependem do evento: montá-los antes
              dispararia requisição sem tenant resolvido. */}
          {!carregandoEvento && (
            <>
              <AddInfoSurvey />
              <GamificationDataInitializer />
              <AnalyticsInitializer />
              <QRCodesWatcher />
              <BonusSheetWatcher />
              <CheckInWatcher />
              <CsatWatcher />
            </>
          )}
        </DigitalProductProvider>
      </>
    )
  }

  if (requiresAuth && sessionIsLoading) {
    // Sem shell de propósito: montá-lo aqui dispararia as queries autenticadas
    // do chrome antes de existir sessão. Uma barra fina não é takeover — o que
    // o redesenho eliminou foi o overlay de tela cheia, não todo indicador.
    return (
      <div
        data-testid="guard-session-loading"
        className="min-h-screen w-screen bg-[var(--color-body-bg)]"
      >
        <TopProgressBar />
      </div>
    )
  }

  if (isBootstrap || isPublicRoute) {
    return (
      <>
        <SiteMeta
          lang="pt_BR"
          defaultTitle={digitalProduct?.name}
          titleTemplate={`%s | ${digitalProduct?.name}`}
          faviconHref={_.get(
            digitalProduct,
            'favicon.file.url',
            '/placeholders/icon.png'
          )}
          description={digitalProduct?.description}
          metaTags={[
            { name: 'viewport', content: 'width=device-width' }
          ]}
        />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {children}
        </div>
      </>
    )
  }

  if (inNativeAppWebView) {
    return (
      <div
        data-testid="guard-native-auth"
        className="min-h-screen w-screen bg-[var(--color-body-bg)]"
      >
        <TopProgressBar />
      </div>
    )
  }

  return (
    <div
      data-testid="guard-auth-redirect"
      className="min-h-screen w-screen bg-[var(--color-body-bg)]"
    >
      <TopProgressBar />
    </div>
  )
}
